/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, Provider } from '@angular/core';
import { XhrFactory } from '@angular/common';
import { noop, Observable, of, ReplaySubject } from 'rxjs';
import { delay, mergeMap } from 'rxjs/operators';
import { extend, isNil, omitBy } from 'lodash';
import { HeaderType } from 'nv-angular-http-endpoint/lib/nv-http-endpoint/nv-http-endpoint';
import { StatusCodeZeroAnalyzerFactoryService } from 'crimson/lib/analyze-status-code-zero/status-code-zero-analyzer-factory.service';
import {
    ErrorAnalysisInfo,
    StatusCodeZeroAnalyzerConfig
} from 'crimson/lib/analyze-status-code-zero/analyze-status-code-zero';
import { StatusCodeZeroAnalyzer } from 'crimson/lib/analyze-status-code-zero/status-code-zero-analyzer';
import { ServiceWorkerService } from 'service-worker/lib/service-worker.service';
import { CommonHeaderNames, SwFetchSucceededParams } from 'service-workers/sw.types';
import { SpanTagNames, ComponentNames, ServiceWorkerSpanPrefix } from '../constants/distributedtracing_constants';
import { APITracingConfig, OpenTelemetrySpan, OpenTelemetryTracer, SpanTags } from '../distributed-tracing';
import { OTELTracerService } from './otel-tracer.service';

interface OpenTracingXMLHttpRequest extends XMLHttpRequest {
    urlInfo?: URL;
    method?: string;
    requestHeaders: { [key: string]: any };
    commonSpanTags: SpanTags;
    tracer: OpenTelemetryTracer;
    span?: OpenTelemetrySpan;
    serviceWorkerSpan?: OpenTelemetrySpan;
    isHandledByServiceWorker?: boolean;
    requestAborted?: boolean;
    uniqueRequestId?: string;
    bypassCacheHeaderValue?: string;

    constructSpanTags(): SpanTags;
    createSpanUsingContext(spanName: string): OpenTelemetrySpan;
    injectCarrierHeaders(): void;
    getOperationName(): string;
    getResponseHeaders(): string[];
    analyzeStatusCodeZero(): Observable<ErrorAnalysisInfo>;
}

const EventNameMap: { [key: number]: string } = {
    [XMLHttpRequest.UNSENT]: 'unsent',
    [XMLHttpRequest.OPENED]: 'opened',
    [XMLHttpRequest.HEADERS_RECEIVED]: 'headers received',
    [XMLHttpRequest.LOADING]: 'loading',
    [XMLHttpRequest.DONE]: 'done'
};

const setStatusCodeTags = (span: OpenTelemetrySpan, statusCode: number): void => {
    span.setAttributes({
        [SpanTagNames.HttpStatusCode]: statusCode,
        [SpanTagNames.HttpStatusCodeFamily]: statusCode.toString().replace(/[0-9]{2}$/, 'XX'),
        [SpanTagNames.Error]: statusCode <= 0 || statusCode >= 400
    });
};

@Injectable({
    providedIn: 'root'
})
export class DistributedTracingXhrFactoryService extends XhrFactory {
    private globalSpanTags: SpanTags = {};
    private tracer: OpenTelemetryTracer = null;
    private tracingEnabledURLRegex: RegExp[];
    private tracingEnabledUrls: Map<string, boolean>;
    private excludedQueryParams = new Set<string>();
    private statusCodeZeroAnalyzer: StatusCodeZeroAnalyzer;
    private pendingNetworkResponseSpanMap: Map<string, OpenTelemetrySpan>;
    private createSpanUsingContext;
    private traceInjectWrapper;

    constructor(
        private statusCodeZeroAnalyzerFactoryService: StatusCodeZeroAnalyzerFactoryService,
        private serviceWorkerService: ServiceWorkerService,
        private otelTracerService: OTELTracerService
    ) {
        super();
        this.tracingEnabledURLRegex = [];
        this.tracingEnabledUrls = new Map<string, boolean>();
        this.pendingNetworkResponseSpanMap = new Map<string, OpenTelemetrySpan>();
        this.createSpanUsingContext = this.otelTracerService.getCreateSpanUsingContextWrapper();
        this.traceInjectWrapper = this.otelTracerService.getTraceInjectWrapper();
    }

    public initialize(
        tracer: OpenTelemetryTracer,
        apiTracingConfig: APITracingConfig,
        statusCodeZeroAnalyzerConfig: StatusCodeZeroAnalyzerConfig,
        globalTagsReplaySubject: ReplaySubject<SpanTags>
    ): void {
        this.tracer = tracer;
        this.setTracingEnabledURLRegex(apiTracingConfig.urlRegex);
        this.excludedQueryParams = new Set(apiTracingConfig.excludedQueryParams ?? []);
        this.setStatusCodeZeroAnalyzerConfig(statusCodeZeroAnalyzerConfig);
        this.serviceWorkerService.networkResponseReceived.subscribe((notificationParams: SwFetchSucceededParams) => {
            this.removeFromSWPendingRequests(notificationParams);
        }, noop);

        globalTagsReplaySubject.asObservable().subscribe(
            (globalTags: SpanTags) => {
                this.globalSpanTags = globalTags;
            },
            _ => {
                this.globalSpanTags = {};
            }
        );
    }

    private addToSWPendingRequests(requestId: string, networkRequestSpan: OpenTelemetrySpan): void {
        this.pendingNetworkResponseSpanMap.set(requestId, networkRequestSpan);
    }

    private removeFromSWPendingRequests(params: SwFetchSucceededParams): void {
        if (this.pendingNetworkResponseSpanMap.has(params.requestId)) {
            const nwSpan = this.pendingNetworkResponseSpanMap.get(params.requestId);
            this.pendingNetworkResponseSpanMap.delete(params.requestId);

            if (params.responseStatusCode === 0) {
                this.statusCodeZeroAnalyzer
                    .getFailureCode({
                        requestUrl: params.requestUrl,
                        uniqueRequestId: params.requestId,
                        isCancelled: false
                    })
                    .subscribe((errorInfo: ErrorAnalysisInfo) => {
                        setStatusCodeTags(nwSpan, errorInfo.statusCode);
                        nwSpan.end(params.responseReceivedTimeMs);
                    }, noop);
            } else {
                setStatusCodeTags(nwSpan, params.responseStatusCode);
                nwSpan.end(params.responseReceivedTimeMs);
            }
        }
    }

    build(): XMLHttpRequest {
        const xhr: OpenTracingXMLHttpRequest = <OpenTracingXMLHttpRequest>new XMLHttpRequest();

        if (!isNil(this.tracer)) {
            xhr.requestHeaders = {};
            xhr.commonSpanTags = this.globalSpanTags;
            xhr.tracer = this.tracer;

            xhr.open = this.wrapOpen(xhr);
            xhr.send = this.wrapSend(xhr);
            xhr.setRequestHeader = this.wrapSetRequestHeader(xhr);
            xhr.constructSpanTags = this.getConstructSpanTags();
            xhr.injectCarrierHeaders = this.getInjectCarrierHeaders();
            xhr.getOperationName = this.getOperationName();
            xhr.createSpanUsingContext = this.createSpanUsingContextWrapper();
            xhr.getResponseHeaders = this.getResponseHeaders();
            xhr.analyzeStatusCodeZero = this.analyzeStatusCodeZero();
            xhr.addEventListener('readystatechange', this.getOnReadyStateChangedListener());
            xhr.addEventListener('abort', this.handleAbort());
        }

        return <any>xhr;
    }

    private handleAbort(): () => void {
        return function abortHandler(): void {
            this.isRequestAborted = true;
        };
    }

    private setTracingEnabledURLRegex(urlRegex: string[]) {
        this.tracingEnabledURLRegex = urlRegex.map(str => new RegExp(str, 'g'));
    }

    private setStatusCodeZeroAnalyzerConfig(config: StatusCodeZeroAnalyzerConfig) {
        if (this.statusCodeZeroAnalyzer) {
            this.statusCodeZeroAnalyzer = null;
        }
        this.statusCodeZeroAnalyzer = this.statusCodeZeroAnalyzerFactoryService.createAnalyzer(config);
    }

    private matchURLAgainstEnabledList(url: string) {
        if (!this.tracingEnabledUrls.has(url)) {
            const index = this.tracingEnabledURLRegex.findIndex(regex => {
                const result = url.match(regex);
                return !isNil(result) && result.length === 1;
            });
            this.tracingEnabledUrls.set(url, index !== -1);
        }
        return this.tracingEnabledUrls.get(url);
    }

    private isURLEnabledForTracing(urlInfo: URL): boolean {
        return !isNil(urlInfo) && this.matchURLAgainstEnabledList(urlInfo.toString());
    }

    private getResponseHeaders(): () => string[] {
        return function getResponseHeaders(): string[] {
            const raw = this.getAllResponseHeaders();
            const parts = raw.replace(/\s+$/, '').split(/\n/);
            for (let i = 0; i < parts.length; i++) {
                parts[i] = parts[i].replace(/\r/g, '').trim();
            }
            return parts;
        };
    }

    private getOperationName(): () => string {
        return function getOperationName(): string {
            return this.method + ' ' + this.urlInfo.hostname + this.urlInfo.pathname;
        };
    }

    private createSpanUsingContextWrapper(): (spanName: string) => OpenTelemetrySpan {
        const createSpanUsingContextFn = this.createSpanUsingContext;
        return function createSpanUsingContext(spanName: string): OpenTelemetrySpan {
            return createSpanUsingContextFn(spanName, this.requestHeaders);
        };
    }

    private analyzeStatusCodeZero(): () => Observable<ErrorAnalysisInfo> {
        const analyzeStatusCodeFn = this.statusCodeZeroAnalyzer.getFailureCode;
        return function analyzeStatusCodeZero(): Observable<ErrorAnalysisInfo> {
            const requestUrl = this.urlInfo.href;
            return analyzeStatusCodeFn({
                requestUrl,
                isCancelled: this.isRequestAborted,
                uniqueRequestId: this.uniqueRequestId || ''
            });
        };
    }

    private getOnReadyStateChangedListener(): () => void {
        return function onReadyStateChange(): void {
            const span = this.uniqueRequestId && this.isHandledByServiceWorker ? this.serviceWorkerSpan : this.span;
            if (!isNil(span)) {
                if (this.readyState === XMLHttpRequest.UNSENT) {
                    span.addEvent(EventNameMap[this.readyState]);
                } else if (this.readyState === XMLHttpRequest.OPENED) {
                    span.addEvent(EventNameMap[this.readyState]);
                } else if (this.readyState === XMLHttpRequest.HEADERS_RECEIVED) {
                    span.addEvent(EventNameMap[this.readyState], {
                        method: this.method,
                        url: this.urlInfo.href,
                        responseHeaders: this.getResponseHeaders()
                    });
                } else if (this.readyState === XMLHttpRequest.LOADING) {
                    span.addEvent(EventNameMap[this.readyState]);
                } else if (this.readyState === XMLHttpRequest.DONE) {
                    span.addEvent(EventNameMap[this.readyState], {
                        method: this.method,
                        url: this.urlInfo.href,
                        statusText: this.statusText,
                        responseType: this.responseType,
                        responseHeaders: this.getResponseHeaders()
                    });

                    if (this.status === 0) {
                        of(true)
                            .pipe(
                                /* To differentiate between aborted requests and other status code 0 errors,
                                 * we need to subscribe to abort event. As XHR abort event gets fired after
                                 * XHR ready state change, we need to introduce some delay.
                                 */
                                delay(100),
                                mergeMap(_ => this.analyzeStatusCodeZero(this.urlInfo.href))
                            )
                            .subscribe((statusInfo: ErrorAnalysisInfo) => {
                                span.setAttributes({ status: statusInfo.statusCode });
                                setStatusCodeTags(span, statusInfo.statusCode);
                                span.end();
                            });
                    } else {
                        span.setAttributes({ status: this.status });
                        setStatusCodeTags(span, this.status);
                        span.end();
                    }
                } else {
                    span.setAttributes({
                        readyState: this.readyState
                    });
                }
            }
        };
    }

    private getConstructSpanTags(): () => SpanTags {
        return function constructSpanTags(): SpanTags {
            const spanTags = {
                [SpanTagNames.Component]: ComponentNames.XmlHttpRequest,
                [SpanTagNames.HttpMethod]: this.method,
                [SpanTagNames.Type]: this.urlInfo.protocol.replace(':', ''),
                [SpanTagNames.HttpHostname]: this.urlInfo.hostname,
                [SpanTagNames.HttpUrl]: this.urlInfo.pathname
            };

            extend(spanTags, this.commonSpanTags);

            const headerKeys = Object.keys(this.requestHeaders);
            headerKeys.forEach(key => {
                spanTags[SpanTagNames.HttpHeadersPrefix + key] = this.requestHeaders[key];
            });

            this.urlInfo.searchParams.forEach((value, key) => {
                spanTags[SpanTagNames.ParamsPrefix + key] = value;
            });

            return spanTags;
        };
    }

    private getInjectCarrierHeaders(): () => void {
        const injectFn = this.traceInjectWrapper;
        return function injectCarrierHeaders(): void {
            const carrierHeaders = injectFn(this.span);
            const keys = Object.keys(carrierHeaders);
            keys.forEach(key => {
                Object.getPrototypeOf(this).setRequestHeader.call(this, key, carrierHeaders[key]);
                this.setRequestHeader(key, carrierHeaders[key]);
            });
        };
    }

    private wrapOpen(
        xhr: OpenTracingXMLHttpRequest
    ): (method: string, url: string, async?: boolean, username?: string, password?: string) => void {
        const proxyOpen = xhr.open;
        const excludedQueryParams = this.excludedQueryParams;

        return function open(method: string, url: string, async?: boolean, username?: string, password?: string): void {
            this.method = method;
            try {
                // Exclude trace data from urlInfo; proxyOpen below still receives the original URL.
                this.urlInfo = new URL(url);
                excludedQueryParams.forEach(key => {
                    if (this.urlInfo.searchParams.has(key)) {
                        this.urlInfo.searchParams.delete(key);
                    }
                });
            } catch (e) {
                this.urlInfo = null;
            }
            /* eslint-disable-next-line  prefer-rest-params */
            proxyOpen.apply(this, arguments);
        };
    }

    private wrapSend(xhr: OpenTracingXMLHttpRequest): (body?: Document | BodyInit | null) => void {
        const proxySend = xhr.send;
        const isOriginEnabledForTracing = this.isURLEnabledForTracing.bind(this);
        const willServiceWorkerHandleRequest = this.serviceWorkerService.willGFNServiceWorkerHandleRequest.bind(this);
        const addToPendingRequests = this.addToSWPendingRequests.bind(this);
        const omitOpentracingHeaders = this.omitOpentracingHeaders;
        return function send(body?: Document | BodyInit | null): void {
            if (isNil(this.urlInfo)) {
                /* eslint-disable-next-line  prefer-rest-params */
                proxySend.apply(this, arguments);
                return;
            }

            this.span = this.createSpanUsingContext(this.getOperationName());
            this.isHandledByServiceWorker = willServiceWorkerHandleRequest(
                this.urlInfo.href,
                this.bypassCacheHeaderValue
            );
            if (this.uniqueRequestId && this.isHandledByServiceWorker) {
                this.serviceWorkerSpan = this.createSpanUsingContext(ServiceWorkerSpanPrefix + this.getOperationName());
                this.serviceWorkerSpan.setAttributes(this.constructSpanTags());
                addToPendingRequests(this.uniqueRequestId, this.span);
            }
            this.requestHeaders = omitOpentracingHeaders(this.requestHeaders);

            if (isOriginEnabledForTracing(this.urlInfo)) {
                this.injectCarrierHeaders();
            }

            this.span.setAttributes(this.constructSpanTags());
            this.span.addEvent('send');
            /* eslint-disable-next-line  prefer-rest-params */
            proxySend.apply(this, arguments);
        };
    }

    private wrapSetRequestHeader(xhr: OpenTracingXMLHttpRequest): (name: string, value: string) => void {
        const proxySetRequestHeader = xhr.setRequestHeader;

        return function setRequestHeader(name: string, value: string): void {
            if (!(
                name === 'Authorization' ||
                name === 'X_LOCAL_SECURITY_COOKIE' ||
                name === CommonHeaderNames.SwCacheKeyAppendString
            )) {
                this.requestHeaders[name] = value;
                if (name === HeaderType.xRequestId) {
                    this.uniqueRequestId = value;
                }
                if (name === CommonHeaderNames.SwCacheBypass) {
                    this.bypassCacheHeaderValue = value;
                }
            }
            if (!name.startsWith('ot-') && !name.startsWith('traceparent')) {
                /* eslint-disable-next-line  prefer-rest-params */
                proxySetRequestHeader.apply(this, arguments);
            }
        };
    }

    /**
     * Removing the opentracing headers after extracting the parent span context.
    Opentracing headers will be added again depending on the enabled list
    This is to avoid false reporting of opentracing headers as tags on the spans
    through constructSpanTags() in a use case where we the url is not enabled for tracing
    and we aren't actually injecting the opentracing headers in the xhr **/
    private omitOpentracingHeaders(requestHeaders: Object): Partial<Object> {
        return omitBy(
            requestHeaders,
            (header: string) => !header.startsWith('ot-') && !header.startsWith('traceparent')
        );
    }
}

export const DistributedtracingXhrFactoryProvider: Provider = {
    provide: XhrFactory,
    useExisting: DistributedTracingXhrFactoryService
};
